import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import notificationService from '../../services/notificationService';

const initialState = {
  notifications: [],
  unreadCount: 0,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  loading: false,
  error: null,
  // Notification Settings (superAdmin only)
  settings: {
    playerRegistration: true,
    playerProfileClaim: true,
    coachRegistration: true,
    scoutRegistration: true,
    requestMoreVideo: false,
  },
  settingsLoading: false,
  settingsError: null,
  updateLoading: false,
};

// Fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async ({ page = 1, limit = 10, role = 'superAdmin' }, { rejectWithValue }) => {
    try {
      const data = await notificationService.getNotifications({ page, limit }, role);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notifications'
      );
    }
  }
);

// Fetch notification settings (superAdmin only)
export const fetchNotificationSettings = createAsyncThunk(
  'notification/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const data = await notificationService.getNotificationSettings();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notification settings'
      );
    }
  }
);

// Update notification settings (superAdmin only)
export const updateNotificationSettings = createAsyncThunk(
  'notification/updateSettings',
  async (triggers, { rejectWithValue }) => {
    try {
      const data = await notificationService.updateNotificationSettings(triggers);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update notification settings'
      );
    }
  }
);

// Mark notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notification/markAsRead',
  async ({ notificationId, role = 'superAdmin' }, { rejectWithValue }) => {
    try {
      await notificationService.markAsRead(notificationId, role);
      return notificationId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark notification as read'
      );
    }
  }
);

// Mark all notifications as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'notification/markAllAsRead',
  async ({ role = 'superAdmin' } = {}, { rejectWithValue }) => {
    try {
      await notificationService.markAllAsRead(role);
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark all notifications as read'
      );
    }
  }
);

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.settingsError = null;
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasMore: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications || [];
        state.unreadCount = action.payload.unreadCount || 0;
        state.pagination = {
          currentPage: action.payload.page || 1,
          totalPages: Math.ceil(
            (action.payload.totalCount || 0) / (action.payload.limit || 10)
          ),
          totalCount: action.payload.totalCount || 0,
          limit: action.payload.limit || 10,
          hasMore:
            (action.payload.page || 1) <
            Math.ceil((action.payload.totalCount || 0) / (action.payload.limit || 10)),
        };
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Notification Settings
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload.triggers || initialState.settings;
        state.settingsError = null;
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload;
      })
      // Update Notification Settings
      .addCase(updateNotificationSettings.pending, (state, action) => {
        state.updateLoading = true;
        state.settingsError = null;
        // Optimistically update the UI
        state.settings = action.meta.arg;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.updateLoading = false;
        // Update with server response
        state.settings = action.payload.triggers || state.settings;
        state.settingsError = null;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.updateLoading = false;
        state.settingsError = action.payload;
        // Revert optimistic update on error - refetch settings
      })
      // Mark Notification as Read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(
          (n) => n._id === action.payload
        );
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      // Mark All Notifications as Read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach((notification) => {
          notification.isRead = true;
        });
        state.unreadCount = 0;
      });
  },
});

export const { clearError, clearNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
